import React, {useContext, useEffect, useState} from 'react';
import {FormikErrors} from 'formik';
import {Step, StepLabel, Stepper} from '@mui/material';
import {AccountContext} from '../../../contexts/AccountContext';
import {GoalFormValues, GoalPartProps} from './utils';
import {TARGET_AGE_MAX, TARGET_AGE_MIN} from './constants';
import {targetAgeBusinessRules} from './GoalAmountsHelper';
import GoalReasons from './GoalReasons';
import GoalAmounts from './GoalAmounts';
import GoalPriorities from './GoalPriorities';

export interface GoalPartsType {
  component: (props: GoalPartProps) => JSX.Element;
  header: string;
}

export type GoalPartsDict = Record<string, GoalPartsType>;

export const complexFlowComponents: GoalPartsDict = {
  goalReasons: {
    component: GoalReasons,
    header: 'Create the goal',
  },
  goalAmounts: {
    component: GoalAmounts,
    header: 'Set the amounts',
  },
  goalPriorities: {
    component: GoalPriorities,
    header: 'Additional Information',
  },
};

export const simpleFlowComponents: GoalPartsDict = {
  goalReasons: {
    component: GoalReasons,
    header: 'Create the goal',
  },
  goalPriorities: {
    component: GoalPriorities,
    header: 'Additional Information',
  },
};

export const useGoalParts = () => {
  const {isFullGoalSteps} = useContext(AccountContext);
  const [parts, setParts] = useState<GoalPartsDict>({...simpleFlowComponents});
  const [maxStep, setMaxStep] = useState(0);

  useEffect(() => {
    const newParts = isFullGoalSteps
      ? {...complexFlowComponents}
      : {...simpleFlowComponents};
    setParts(newParts);
    setMaxStep(Object.keys(newParts).length - 1);
  }, [isFullGoalSteps]);

  return {parts, maxStep};
};

export function getSubmitGoalFunction(
  creating: boolean,
  first: (this_values: GoalFormValues) => Promise<void>,
  second: (this_values: GoalFormValues) => Promise<void>,
  afterSubmit: () => void
) {
  const call = creating ? first : second;
  return (values: GoalFormValues) => {
    call(values).then(() => afterSubmit());
  };
}

export function goalValidation(values: GoalFormValues) {
  const errors: FormikErrors<GoalFormValues> = {};

  if (values.targetAge) {
    try {
      const targetAge = Number(values.targetAge);
      const shouldValidateTargetAge = targetAgeBusinessRules(values);
      if (shouldValidateTargetAge) {
        if (Number.isNaN(targetAge)) {
          throw Error();
        }
        if (targetAge < TARGET_AGE_MIN) {
          errors.targetAge = `Target age must be at least ${TARGET_AGE_MIN}`;
        }
        if (targetAge > TARGET_AGE_MAX) {
          errors.targetAge = `Target age must be at under ${TARGET_AGE_MAX}`;
        }
      }
    } catch (err) {
      errors.targetAge = 'Target age must be a number';
    }
  }
  if (values.targetYears) {
    try {
      const targetYears = Number(values.targetYears);
      if (Number.isNaN(targetYears)) {
        throw Error();
      }
      if (targetYears < 1) {
        errors.targetYears = `Target years must be positive`;
      }
    } catch (err) {
      errors.targetYears = 'Target years must be a number';
    }
  }
  return errors;
}

export interface EditGoalStepperProps {
  activeStep: number;
  steps: string[];
}

export function EditGoalStepper(props: EditGoalStepperProps) {
  const {activeStep, steps} = props;

  return (
    <div
      style={{
        width: '100%',
      }}
    >
      <Stepper activeStep={activeStep}>
        {steps.map((label) => {
          const stepProps = {};
          const labelProps = {};
          return (
            <Step key={label} {...stepProps}>
              <StepLabel {...labelProps}>{label}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
    </div>
  );
}
